import { useEffect, useState } from 'react';
import { pushConfigDraftsService } from 'lib/services/pushConfigDraftsService';
import {
  PushConfigDay,
  pushConfigService,
} from 'lib/services/pushConfigService';
import { showToast } from '../toast';
import { DAYS_OF_WEEK } from '../enums';

const { getPushConfigRequest } = pushConfigService();
const { getPushConfigDraftsRequest } = pushConfigDraftsService();

type PushConfigDayCompareDay = {
  id: string;
  day: DAYS_OF_WEEK;
  specificDay: boolean;
  specificDayTitle: null | string;
  specificDayMessage: null | string;
  tabType: string;
  cardType: string;
  actionHappyTabsType: string | null;
  actionTabularyTabsType: string | null;
  unflipAllTabs: boolean | null;
  deliveryTime: string;
  active: boolean;
  draft?: Partial<{
    id: string;
    day: DAYS_OF_WEEK;
    specificDay: boolean;
    specificDayTitle: null | string;
    specificDayMessage: null | string;
    tabType: string;
    cardType: string;
    actionHappyTabsType: string | null;
    actionTabularyTabsType: string | null;
    unflipAllTabs: boolean | null;
    deliveryTime: string;
    active: boolean;
  }>;
};

export type PushConfigDayCompare = {
  type: string;
  dayTitle: string;
  dayMessage: string;
  tabType: string;
  cardType: string;
  actionHappyTabsType: string | null;
  actionTabularyTabsType: string | null;
  unflipAllTabs: boolean | null;
  specificDay: boolean;
  deliveryTime: string;
  active: boolean;
  days: ReadonlyArray<PushConfigDayCompareDay>;
  draft: Partial<{
    dayTitle: string;
    dayMessage: string;
    tabType: string;
    cardType: string;
    actionHappyTabsType: string | null;
    actionTabularyTabsType: string | null;
    unflipAllTabs: boolean | null;
    specificDay: boolean;
    deliveryTime: string;
    active: boolean;
  }>;
};

export const usePushManagementCompare = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [pushConfig, setPushConfig] = useState<
    ReadonlyArray<PushConfigDayCompare>
  >([]);

  const getCurrentNotifications = async () => {
    setIsLoading(true);

    try {
      const [pushConfigResponseLive, pushConfigResponseDraft] =
        await Promise.all([
          getPushConfigRequest(),
          getPushConfigDraftsRequest(),
        ]);

      const liveConfig = pushConfigResponseLive.data['hydra:member'];
      const draftConfig = pushConfigResponseDraft.data['hydra:member'];

      const compareConfigs = liveConfig.reduce<
        ReadonlyArray<PushConfigDayCompare>
      >((acc, liveItem, index) => {
        const draftItem = draftConfig.find(
          ({ type }) => type === liveItem.type,
        ) as PushConfigDay;

        let changes: any = {};
        let hasChanges = false;

        const listOfFields = [
          'dayTitle',
          'dayMessage',
          'tabType',
          'cardType',
          'actionHappyTabsType',
          'actionTabularyTabsType',
          'unflipAllTabs',
          'specificDay',
          'deliveryTime',
          'active',
        ];

        listOfFields.forEach((field) => {
          // @ts-ignore
          if (liveItem[field] !== draftItem[field]) {
            // @ts-ignore
            changes[field] = draftItem[field];
            hasChanges = true;
          }
        });

        const days = liveItem.days.reduce<
          ReadonlyArray<PushConfigDayCompareDay>
        >((daysAcc, liveDayItem, index) => {
          if (!liveItem.specificDay && !draftItem.specificDay) {
            return daysAcc;
          }

          const draftDayItem = draftItem.days[index];

          let dayChanges: any = {};
          let dayHasChanges = false;

          const listOfFields = [
            'specifyDayTitle',
            'specifyDayMessage',
            'tabType',
            'cardType',
            'actionHappyTabsType',
            'actionTabularyTabsType',
            'unflipAllTabs',
            'deliveryTime',
            'active',
          ];

          listOfFields.forEach((field) => {
            // @ts-ignore
            if (liveDayItem[field] !== draftDayItem[field]) {
              // @ts-ignore
              dayChanges[field] = draftDayItem[field];
              dayHasChanges = true;
            }
          });

          if (dayHasChanges || changes.specificDay) {
            hasChanges = true;

            return [
              ...daysAcc,
              {
                ...liveDayItem,
                draft: dayChanges,
              },
            ];
          }

          return daysAcc;
        }, []);

        if (hasChanges) {
          return [
            ...acc,
            {
              ...liveItem,
              days,
              draft: changes,
            },
          ];
        }

        return acc;
      }, []);

      setPushConfig(compareConfigs);
    } catch (e) {
      console.error(e);
      showToast({
        type: 'error',
        title: `Error`,
        message: 'Cannot access config',
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getCurrentNotifications();
  }, []);

  return {
    pushConfig,
    isLoading,
  };
};

export default usePushManagementCompare;
